import {
  objectsToCSV,
  downloadCSV,
  formatDateForCSV,
  formatBooleanForCSV,
} from "@/lib/utils/export/csv-export";
import { toast } from "sonner";
import type { SimpleFieldConfig, EntityWithRelations } from "../types";

// ============================================================================
// CSV EXPORT FUNCTIONALITY
// ============================================================================

/**
 * Handles CSV export of processed data
 */
export const handleExportCSV = (
  processedData: EntityWithRelations[],
  visibleFields: [string, SimpleFieldConfig][],
  config: { displayName: string }
): void => {
  if (!processedData.length) {
    toast.error("No data to export");
    return;
  }

  // Prepare data for export
  const exportData = processedData.map((item) => {
    const exportItem: Record<string, any> = {};
    visibleFields.forEach(([fieldId, fieldConfig]) => {
      const value = item[fieldId as keyof typeof item];
      switch (fieldConfig.type) {
        case "date":
          exportItem[fieldConfig.label] = formatDateForCSV(
            (value instanceof Date || typeof value === "string") ? value : ""
          );
          break;
        case "datetime":
          exportItem[fieldConfig.label] =
            value && (typeof value === "string" || typeof value === "number" || value instanceof Date)
              ? new Date(value).toISOString()
              : "";
          break;
        case "time":
          // Export time in HH:MM format
          if (value && typeof value === "string") {
            const timeValue = value.includes("T") ? value.split("T")[1] : value;
            exportItem[fieldConfig.label] = timeValue.substring(0, 5);
          } else {
            exportItem[fieldConfig.label] = "";
          }
          break;
        case "boolean":
          exportItem[fieldConfig.label] = formatBooleanForCSV(
            typeof value === "boolean" ? value : undefined
          );
          break;
        default:
          exportItem[fieldConfig.label] = value || "";
      }
    });
    return exportItem;
  });

  const csvContent = objectsToCSV(exportData);
  const filename = `${config.displayName.toLowerCase().replace(/\s+/g, "_")}_export_${new Date().toISOString().split("T")[0]}.csv`;

  downloadCSV(csvContent, filename);
  toast.success(`Exported ${exportData.length} records to ${filename}`);
};
